import { useState } from "react";
import { Chat } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";
import { Box, Button, Chip, CircularProgress, Divider, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Comments, FileUpload, StatusChip } from "../../../shared/components";
import { useFetchTaskDetails } from "../hooks/useFetchTaskDetails";
import { getPriorityConfig } from "../utils/getPriorityConfig";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const TaskDetails = ({ taskId, onEdit, onClose }: { taskId: string, onEdit: () => void, onClose: () => void }) => {

    const { isAllowedToEditTask } = useCapabilities();
    const { taskDetails, isPending, isError, error } = useFetchTaskDetails({ taskId });

    const { color, icon, label } = getPriorityConfig(taskDetails?.priority);

    const tags = Array.isArray(taskDetails.tags) ? taskDetails.tags.filter((value: string) => {
        return value.trim() !== "";
    }) : taskDetails.tags ? taskDetails.tags.split(",").filter((t: string) => t) : [];

    const visibleTags = tags.slice(0, 2);
    const remainingTags = tags.length - 2;
    const commentCount = taskDetails.comment_count || 0;

    const [currentTab, setCurrentTab] = useState('overview');
    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    if (isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <Typography variant="h6" color="error">
                    {error?.message || "Error loading data."}
                </Typography>
            </Box>
        )
    };

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'}>
            <TabContext value={currentTab}>
                <Box display="flex" alignItems="center" alignContent="center">
                    <Tabs
                        value={currentTab}
                        onChange={handleChangeTab}
                        aria-label="Tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ minHeight: 'auto' }}
                    >
                        <Tab sx={{ height: 'auto', minHeight: 'auto' }} label={'overview'} value={'overview'} iconPosition="start" />
                        {/* <Tab sx={{ height: 'auto', minHeight: 'auto' }} label={'sub tasks'} value={'sub-tasks'} iconPosition="start" disabled /> */}
                        {/* <Tab sx={{ height: 'auto', minHeight: 'auto' }} label={'history'} value={'history'} iconPosition="start" disabled /> */}
                        <Tab sx={{ height: 'auto', minHeight: 'auto' }} label={'comments'} value={'comments'} iconPosition="start" />
                    </Tabs>
                </Box>
                <Divider />
                <Box flex={1} overflow="hidden">
                    <TabPanel value="overview" sx={{ p: 2, height: "100%" }}>
                        <Box display={'flex'} flexDirection={'column'} height={'100%'}>
                            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2}>
                                <Box border={1} borderColor={"divider"} borderRadius={1} p={2} display={'flex'} flexDirection={'column'} gap={1}>
                                    <Box display={'flex'} alignItems={'center'} gap={2}>
                                        <Typography variant="caption" color="text.secondary">{taskDetails?.task_ref}</Typography>
                                        <StatusChip label={taskDetails?.status_display_text || ""} color={"amber"} />
                                    </Box>
                                    <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                                        <Typography variant="body1" color="text.primary">{taskDetails?.task_name}</Typography>
                                        {label && (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={0.5}
                                                sx={{ color: color, flexShrink: 0 }}
                                            >
                                                {icon}
                                                <Typography variant="caption" fontWeight="bold">
                                                    {label}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">{taskDetails?.description || 'N/A'}</Typography>
                                    {(
                                        tags.length > 0 ? (
                                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                {visibleTags.map((tag: string, index: number) => (
                                                    <Chip key={index} label={tag} size="small" sx={{ color: "text.secondary", borderRadius: 1 }} />
                                                ))}
                                                {remainingTags > 0 && (
                                                    <Chip label={`+${remainingTags}`} size="small" sx={{ color: "text.secondary", borderRadius: 1 }} />
                                                )}
                                            </Box>
                                        ) :
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                No tags assigned
                                            </Typography>
                                    )}
                                    <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1.5}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {taskDetails?.task_ref}
                                        </Typography>
                                        <Box display="flex" alignItems="center" ml={"auto"} gap={0.5}>
                                            <Chat
                                                sx={{ fontSize: 16, color: "text.secondary" }}
                                            />
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                fontWeight="medium"
                                            >
                                                {commentCount}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                width: 25,
                                                height: 25,
                                                borderRadius: "50%",
                                                bgcolor: "divider",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "success.contrastText",
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {taskDetails?.owner?.split(" ").filter(Boolean).map((word: string[]) => word[0].toUpperCase()).join("").slice(0, 2)}
                                        </Box>
                                    </Box>
                                    <FileUpload type={"tasks"} typeId={taskId} isDisableDelete={true} isDisableEdit={true} />
                                </Box>
                            </Box>
                            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0}>
                                <Button variant="outlined" onClick={onClose}>Close</Button>
                                {
                                    isAllowedToEditTask && (
                                        <Button variant="contained" onClick={onEdit}>Edit task</Button>
                                    )
                                }
                            </Stack>
                        </Box>
                    </TabPanel>
                    <TabPanel value="comments" sx={{ p: 2, height: "100%" }}>
                        <Comments topic={['TASKS']} sub_event={['TASK_COMMENT']} object_id={taskId} parent_id={undefined} />
                    </TabPanel>
                </Box>
            </TabContext>
        </Box>
    );
}